import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import * as eventsService from '../services/eventsService';
import * as leadsService from '../services/leadsService';
import * as dashboardService from '../services/dashboardService';
import * as registrationsService from '../services/registrationsService';

const EventContext = createContext();
const DASHBOARD_TIMEOUT_MS = 10000;

const mapEventRecord = (event) => ({
  id: event.id,
  title: event.title ?? '',
  description: event.description ?? '',
  date: event.date ?? '',
  time: event.start_time ?? event.time ?? '',
  duration: event.duration ?? 0,
  price: event.price ?? 0,
  tieredPricing: event.tiered_pricing ?? event.tieredPricing ?? false,
  zoomLink: event.zoom_link ?? event.zoomLink ?? '',
  agentId: event.agent_id ?? event.agentId ?? null,
  status: event.status ?? 'draft',
  registrations: event.registrations ?? event.registration_count ?? 0,
  maxAttendees: event.max_attendees ?? event.maxAttendees ?? event.capacity ?? null,
  templateId: event.template_id ?? event.templateId ?? null,
  customTemplate: event.custom_template ?? event.customTemplate ?? null,
  resources: event.resources ?? [],
  recordingUrl: event.recording_url ?? event.recordingUrl ?? null,
  createdAt: event.created_at ?? event.createdAt ?? null,
  usePlatformAccount: event.use_platform_account ?? event.usePlatformAccount ?? false
});

const mapLeadRecord = (lead) => {
  const resolvedEvent = lead.events ?? {};
  const resolvedFullName = lead.full_name ?? lead.fullName ?? lead.name ?? '';
  const asl = lead.agent_sharing_links ?? null;
  const sharingLink = Array.isArray(asl) ? asl[0] : asl;

  return {
    id: lead.id,
    eventId: lead.event_id ?? lead.eventId ?? null,
    eventTitle: resolvedEvent.title ?? lead.eventTitle ?? '',
    eventPrice: resolvedEvent.price != null ? Number(resolvedEvent.price) : null,
    fullName: resolvedFullName,
    name: resolvedFullName,
    email: lead.email ?? '',
    phone: lead.phone ?? '',
    invitedBy: lead.invited_by ?? lead.invitedBy ?? '',
    status: lead.status ?? 'new',
    notes: lead.notes ?? '',
    meetings: Array.isArray(lead.meetings) ? lead.meetings : [],
    registeredAt: lead.registered_at ?? lead.created_at ?? lead.registeredAt ?? null,
    createdAt: lead.created_at ?? lead.registered_at ?? lead.createdAt ?? null,
    agentId: lead.agent_id ?? lead.agentId ?? null,
    primaryAgentId: lead.primary_agent_id ?? lead.primaryAgentId ?? null,
    archived: lead.archived ?? false,
    paymentStatus: lead.payment_status ?? 'pending',
    paymentAmount: lead.payment_amount != null ? Number(lead.payment_amount) : null,
    paymentDate: lead.payment_date ?? null,
    accountCreated: lead.account_created ?? false,
    accountCreatedAt: lead.account_created_at ?? null,
    studentId: lead.student_id ?? null,
    sharingLinkId: lead.sharing_link_id ?? null,
    sharingLinkSlug: sharingLink?.slug ?? lead.sharingLinkSlug ?? null,
    sharingLinkIdentifier: sharingLink?.identifier ?? lead.sharingLinkIdentifier ?? null
  };
};

const deriveStats = (eventsList = [], leadsList = []) => {
  const totalEvents = eventsList.length;
  const totalLeads = leadsList.length;
  const oneOnOneCompleted = leadsList.filter(lead => lead.status === '1_1_completed').length;
  const converted = leadsList.filter(lead => lead.status === 'converted').length;
  const conversionRate = totalLeads > 0 ? (converted / totalLeads) * 100 : 0;

  return {
    totalEvents,
    totalLeads,
    oneOnOneCompleted,
    conversionRate
  };
};

/** Dashboard/aggregate events and leads (from EventProvider). For list/promotable events use hooks/useEvents. */
export const useDashboardEvents = () => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useDashboardEvents must be used within an EventProvider');
  }
  return context;
};

export const EventProvider = ({ children }) => {
  const {
    profile,
    role,
    authReady,
    loading: authLoading,
    profileLoading
  } = useAuth();
  const [events, setEvents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(() => deriveStats([], []));

  const fetchWithTimeout = useCallback(async (label, promise, options = {}) => {
    const { logError = true } = options;
    console.log(`[Dashboard] ${label} query started.`);
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`${label} query timed out after ${DASHBOARD_TIMEOUT_MS}ms`));
        }, DASHBOARD_TIMEOUT_MS);
      });

      const result = await Promise.race([promise, timeoutPromise]);
      if (result?.error) {
        throw result.error;
      }
      console.log(`[Dashboard] ${label} query finished.`);
      return result?.data ?? null;
    } catch (fetchError) {
      if (logError) {
        console.error(`[Dashboard] ${label} query error.`, fetchError);
      }
      throw fetchError;
    }
  }, []);

  const fetchDashboardData = useCallback(async (profileId, profileRole) => {
    if (!profileId || !profileRole) {
      console.warn('[Dashboard] fetchDashboardData aborted: missing profileId/role', {
        profileId,
        profileRole
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    console.log('[Dashboard] fetchDashboardData started.', { profileId, profileRole });

    try {
      if (profileRole === 'student') {
        // Students: fetch their registrations, then events for those event IDs only
        const registrationsResult = await fetchWithTimeout(
          'student registrations',
          registrationsService.listRegistrations({ role: profileRole, userId: profileId })
        ).catch((err) => {
          console.error('[Dashboard] Student registrations error.', err);
          throw err;
        });

        const studentEventIds = registrationsResult && Array.isArray(registrationsResult)
          ? [...new Set(registrationsResult.map((r) => r.event_id).filter(Boolean))]
          : [];

        const eventsResult = await fetchWithTimeout(
          'events (student)',
          eventsService.listEvents({ role: profileRole, userId: profileId, studentEventIds })
        ).catch((err) => {
          console.error('[Dashboard] Student events error.', err);
          throw err;
        });

        const mappedEvents = eventsResult && Array.isArray(eventsResult)
          ? eventsResult.map(mapEventRecord)
          : [];

        setEvents(mappedEvents);
        setLeads([]);
        setStats(deriveStats(mappedEvents, []));
        setError(null);
        console.log('[Dashboard] fetchDashboardData finished (student).', { eventCount: mappedEvents.length });
        return;
      }

      const [eventsResult, leadsResult, statsResult] = await Promise.allSettled([
        fetchWithTimeout(
          'events',
          eventsService.listEvents({ role: profileRole, userId: profileId })
        ),
        fetchWithTimeout(
          'leads',
          leadsService.listLeads({ role: profileRole, userId: profileId })
        ),
        fetchWithTimeout(
          'dashboard stats',
          dashboardService.getDashboardStats({ role: profileRole, userId: profileId }),
          { logError: false }
        )
      ]);

      let mappedEvents = eventsResult.status === 'fulfilled'
        ? eventsResult.value.map(mapEventRecord)
        : null;

      // Merge registration counts from event_registrations so list shows correct "N/∞"
      if (mappedEvents && mappedEvents.length > 0 && profileRole !== 'student') {
        const eventIds = mappedEvents.map((e) => e.id);
        try {
          const countsResult = await registrationsService.getRegistrationCountsForEventIds(eventIds, {
            role: profileRole,
            userId: profileId
          });
          if (countsResult.data && countsResult.data.length > 0) {
            const countByEventId = countsResult.data.reduce((acc, row) => {
              const id = row.event_id;
              acc[id] = (acc[id] || 0) + 1;
              return acc;
            }, {});
            mappedEvents = mappedEvents.map((e) => ({
              ...e,
              registrations: countByEventId[e.id] ?? e.registrations ?? 0
            }));
          }
        } catch (countErr) {
          console.warn('[Dashboard] Registration counts unavailable.', countErr);
        }
      }
      const mappedLeads = leadsResult.status === 'fulfilled'
        ? leadsResult.value.map(mapLeadRecord)
        : null;

      if (eventsResult.status === 'fulfilled') {
        setEvents(mappedEvents);
      } else {
        setError(eventsResult.reason?.message ?? 'Unable to load events.');
      }

      if (leadsResult.status === 'fulfilled') {
        setLeads(mappedLeads);
      } else {
        setError(leadsResult.reason?.message ?? 'Unable to load leads.');
      }

      if (statsResult.status === 'fulfilled' && statsResult.value) {
        setStats(statsResult.value);
      } else {
        console.warn('[Dashboard] Dashboard stats unavailable. Using derived stats fallback.');
        setStats(deriveStats(mappedEvents ?? [], mappedLeads ?? []));
      }

      console.log('[Dashboard] fetchDashboardData finished.');
    } catch (fetchError) {
      setError(fetchError?.message ?? 'Unable to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, [fetchWithTimeout]);

  const profileId = profile?.id ?? null;
  const profileRole = profile?.role ?? null;

  useEffect(() => {
    if (!authReady || authLoading || profileLoading || !profileId || !profileRole) {
      return;
    }

    fetchDashboardData(profileId, profileRole);
  }, [authReady, authLoading, profileLoading, fetchDashboardData, profileId, profileRole]);

  /**
   * Create a new event using the events service (persists to database)
   */
  const createEvent = useCallback(async (eventData) => {
    console.log('[EventContext] createEvent called', eventData);

    const { data, error } = await eventsService.createEvent(eventData, {
      role,
      userId: profile?.id
    });

    if (error) {
      console.error('[EventContext] createEvent failed:', error);
      throw error;
    }

    // Update local state with the new event from database
    const mappedEvent = mapEventRecord(data);
    setEvents(prev => [...prev, mappedEvent]);
    console.log('[EventContext] createEvent success:', mappedEvent);

    return { data: mappedEvent, error: null };
  }, [role, profile?.id]);

  /**
   * Update an existing event using the events service (persists to database)
   */
  const updateEvent = useCallback(async (eventId, updates) => {
    console.log('[EventContext] updateEvent called', { eventId, updates });

    const { data, error } = await eventsService.updateEvent(eventId, updates, {
      role,
      userId: profile?.id
    });

    if (error) {
      console.error('[EventContext] updateEvent failed:', error);
      throw error;
    }

    // Update local state with the updated event from database
    const mappedEvent = mapEventRecord(data);
    setEvents(prev => prev.map(event =>
      event.id === eventId ? mappedEvent : event
    ));
    console.log('[EventContext] updateEvent success:', mappedEvent);

    return { data: mappedEvent, error: null };
  }, [role, profile?.id]);

  /**
   * Delete an event using the events service (persists to database)
   */
  const deleteEvent = useCallback(async (eventId) => {
    console.log('[EventContext] deleteEvent called', eventId);

    const { data, error } = await eventsService.deleteEvent(eventId, {
      role,
      userId: profile?.id
    });

    if (error) {
      console.error('[EventContext] deleteEvent failed:', error);
      throw error;
    }

    // Remove from local state
    setEvents(prev => prev.filter(event => event.id !== eventId));
    console.log('[EventContext] deleteEvent success:', eventId);

    return { data, error: null };
  }, [role, profile?.id]);

  /**
   * Register for an event - creates a lead in the database
   */
  const registerForEvent = useCallback(async (eventId, registrationData) => {
    console.log('[EventContext] registerForEvent called', { eventId, registrationData });

    // Map frontend field names to database field names
    const leadPayload = {
      event_id: eventId,
      full_name: registrationData.name,
      email: registrationData.email,
      phone: registrationData.phone || null,
      invited_by: registrationData.invitedBy || null,
      agent_id: registrationData.agentId || null,
      status: 'new'
    };

    const { data, error } = await leadsService.createLead(leadPayload, {
      role: role || 'student',
      userId: profile?.id
    });

    if (error) {
      console.error('[EventContext] registerForEvent failed:', error);
      throw error;
    }

    // Update local state with the new lead from database
    const mappedLead = mapLeadRecord(data);
    setLeads(prev => [...prev, mappedLead]);

    // Update event registration count locally
    setEvents(prev => prev.map(event =>
      event.id === eventId
        ? { ...event, registrations: (event.registrations || 0) + 1 }
        : event
    ));

    console.log('[EventContext] registerForEvent success:', mappedLead);
    return { data: mappedLead, error: null };
  }, [role, profile?.id]);

  /**
   * Update a lead using the leads service (persists to database)
   */
  const updateLead = useCallback(async (leadId, updates) => {
    console.log('[EventContext] updateLead called', { leadId, updates });

    // Map frontend field names to database field names if needed
    const dbUpdates = {
      ...updates,
      full_name: updates.name ?? updates.full_name,
      agent_id: updates.agentId ?? updates.agent_id,
      event_id: updates.eventId ?? updates.event_id,
      invited_by: updates.invitedBy ?? updates.invited_by
    };
    // Remove frontend-only fields
    delete dbUpdates.name;
    delete dbUpdates.agentId;
    delete dbUpdates.eventId;
    delete dbUpdates.invitedBy;

    const { data, error } = await leadsService.updateLead(leadId, dbUpdates, {
      role,
      userId: profile?.id
    });

    if (error) {
      console.error('[EventContext] updateLead failed:', error);
      throw error;
    }

    // Update local state with the updated lead from database
    const mappedLead = mapLeadRecord(data);
    setLeads(prev => prev.map(lead =>
      lead.id === leadId ? mappedLead : lead
    ));
    console.log('[EventContext] updateLead success:', mappedLead);

    return { data: mappedLead, error: null };
  }, [role, profile?.id]);

  /**
   * Merge partial updates into a lead in local dashboard state (no API call).
   */
  const patchLead = useCallback((leadId, updates) => {
    setLeads((prev) =>
      prev.map((lead) => (lead.id === leadId ? { ...lead, ...updates } : lead))
    );
  }, []);

  /**
   * Remove a lead from the active dashboard list in local state (no API call).
   */
  const removeLeadFromList = useCallback((leadId) => {
    setLeads((prev) => prev.filter((lead) => lead.id !== leadId));
  }, []);

  /**
   * Insert or merge a lead into the active dashboard list (no API call).
   */
  const upsertLeadInList = useCallback((leadRecord) => {
    const mapped = mapLeadRecord(leadRecord);
    setLeads((prev) => {
      const index = prev.findIndex((lead) => lead.id === mapped.id);
      if (index === -1) {
        return [...prev, mapped];
      }
      const next = [...prev];
      next[index] = { ...next[index], ...mapped };
      return next;
    });
  }, []);

  /**
   * Archive a lead (soft delete). Removes from main list after success.
   */
  const archiveLead = useCallback(async (leadId) => {
    const { data, error } = await leadsService.archiveLead(leadId, {
      role,
      userId: profile?.id
    });
    if (error) throw error;
    setLeads((prev) => prev.filter((lead) => lead.id !== leadId));
    return { data, error: null };
  }, [role, profile?.id]);

  /**
   * Unarchive a lead. Adds back to main list after success.
   */
  const unarchiveLead = useCallback(async (leadId) => {
    const { data, error } = await leadsService.unarchiveLead(leadId, {
      role,
      userId: profile?.id
    });
    if (error) throw error;
    const mapped = mapLeadRecord(data);
    setLeads((prev) => [...prev, mapped]);
    return { data: mapped, error: null };
  }, [role, profile?.id]);

  /**
   * Permanently delete a lead. Admin only.
   */
  const deleteLead = useCallback(async (leadId) => {
    const { data, error } = await leadsService.deleteLead(leadId, {
      role,
      userId: profile?.id
    });
    if (error) throw error;
    setLeads((prev) => prev.filter((lead) => lead.id !== leadId));
    return { data, error: null };
  }, [role, profile?.id]);

  const value = {
    events,
    leads,
    stats,
    loading,
    error,
    fetchDashboardData,
    createEvent,
    updateEvent,
    deleteEvent,
    registerForEvent,
    updateLead,
    patchLead,
    removeLeadFromList,
    upsertLeadInList,
    archiveLead,
    unarchiveLead,
    deleteLead
  };

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
};
